import { create } from "zustand";
import { parseSasToken, SasTokenInfo } from "../services/sasTokenParser";
import {
  createAzureStorageClient,
  StorageClient,
  testCorsConnectivity,
} from "../services/azureBlobService";

// Constants for URL parameter management
const URL_PARAM_NAME = "containerUrl";

const setUrlParam = (name: string, value: string) => {
  const url = new URL(window.location.href);
  if (value) {
    url.searchParams.set(name, value);
  } else {
    url.searchParams.delete(name);
  }
  window.history.replaceState({}, '', url.toString());
};

export interface Container {
  id: string;
  status: "loading" | "error" | "ready" | "uninitialized";
  url?: string;
  corsStatus?: { success: boolean; error?: string };
  sasToken?: SasTokenInfo;
  client?: StorageClient;
  info?: string;

  isReady: () => boolean;
  hasError: () => boolean;
  isUninitialized: () => boolean;
}

export const useContainer = create<Container>((set, get) => ({
  id: "",
  status: "uninitialized",
  url: undefined,
  corsStatus: undefined,
  sasToken: undefined,
  client: undefined,
  info: "Set url to open a configured container",
  isReady: () => get().status === "ready",
  hasError: () => get().status === "error",
  isUninitialized: () => get().status === "uninitialized",
}));

export const setContainerState = (newState: Partial<Container>) => {
  useContainer.setState((state) => ({
    ...state,
    ...newState,
  }));
};

export const setContainer = (container: Container) => {
  useContainer.setState(container);
};

export const getContainerActions = () => {
  return {
    setContainerUrl: async (url: string) => {
      if (useContainer.getState().url === url) {
        // URL is already set, no need to re-validate
        return;
      }
      console.log("Setting container URL:", url);
      if(url.trim() === "") { 
        console.log("Clearing container state due to empty URL");

        // Clear URL parameter from browser URL when resetting
        setUrlParam(URL_PARAM_NAME, "");
        
        setContainerState({
          status: "uninitialized",
          url: undefined,
          corsStatus: undefined,
          sasToken: undefined,
          client: undefined,
          info: "Set url to open a configured container",
        });
        return;
      }      // Set loading state
      setContainerState({
        status: "loading",
        url: url,
        info: "Validating URL and testing CORS...",
      });

      const sasToken = parseSasToken(url);
      const corsCheckResult = await testCorsConnectivity(url);
   
      if (sasToken?.status !== "valid" || !corsCheckResult.success) {
        
        const sasErrorMessage = sasToken?.status !== "valid" ? "Invalid SAS Token." : "";
        const corsErrorMessage = !corsCheckResult.success ? (corsCheckResult.error || "CORS test failed.") : "";

        setContainerState({
          status: "error",
          url: url,
          corsStatus: corsCheckResult,
          sasToken: sasToken || undefined,
          info: "Error: " + (sasErrorMessage + " " + corsErrorMessage).trim(),
        });
        return;
      }

      const client = createAzureStorageClient(url);
      console.log("Container URL validated and CORS test passed.");
      setUrlParam(URL_PARAM_NAME, url);
      setContainerState({
        status: "ready",
        url: url,
        corsStatus: corsCheckResult,
        sasToken: sasToken,
        client: client,
        info: "Container is ready.",
      });
    },
    
    // Reset container and clear URL from browser
    resetContainer: () => {
      console.log("Resetting container and clearing URL from browser");
      setUrlParam(URL_PARAM_NAME, "");
      setContainerState({
        status: "uninitialized",
        url: undefined,
        corsStatus: undefined,
        sasToken: undefined,
        client: undefined,
        info: "Set url to open a configured container",
      });
    },
  };
};

export const useContainerState = () => {
  return {
    isErrored: useContainer((state) => state.status === "error"),
    isLoading: useContainer((state) => state.status === "loading"),
    isUninitialized: useContainer((state) => state.status === "uninitialized"),
    isReady: useContainer((state) => state.status === "ready"),
    info: useContainer((state) => state.info),
    status: useContainer((state) => state.status),
  };
};

// Hook to get container actions including reset
export const useContainerActions = () => {
  return getContainerActions();
};
